import { IState } from 'appRedux/createStore';
import { canCreateCasinoAccount } from 'pages/CasinoAccounts/createCasinoAccountAccess';
import { canAccessDeleteCasinoAccounts } from 'pages/DeleteCasinoAccounts/deleteCasinoAccountsAccess';
import { canAccessManufacturersManagement } from 'pages/ManufacturersManagement/manufacturersManagementAccess';
import { canAccessGameNamesManagement } from 'pages/GameNamesManagement/gameNamesManagementAccess';
import { useSelector } from 'react-redux';
import { Redirect, Route } from 'react-router';

interface ProtectedRouteProps {
  component: React.ComponentClass<any> | React.FunctionComponent<any>;
  path: string;
}

export const ProtectedRoute = ({ component: Comp, path, ...rest }: ProtectedRouteProps) => {
  const isSlotcheckInternal = useSelector<IState>((state) =>
    state.app.persistedState.userInfo?.groups?.includes('SlotcheckInternal')
  );

  return (
    <Route
      path={path}
      {...rest}
      render={(props) => {
        return isSlotcheckInternal ? <Comp {...props} /> : <Redirect to="/" />;
      }}
    />
  );
};

export const ProtectedRouteCasinoAdmin = ({
  component: Comp,
  path,
  ...rest
}: ProtectedRouteProps) => {
  const isLOTCAdmin = useSelector<IState>((state) =>
    state.app.persistedState.userInfo?.groups?.includes('LOTCAdmin')
  );
  const isEurekaAdmin = useSelector<IState>((state) =>
    state.app.persistedState.userInfo?.groups?.includes('EurekaAdmin')
  );

  return (
    <Route
      path={path}
      {...rest}
      render={(props) => {
        return isLOTCAdmin || isEurekaAdmin ? <Comp {...props} /> : <Redirect to="/" />;
      }}
    />
  );
};

export const ProtectedRouteEnterpriseReporting = ({
  component: Comp,
  path,
  ...rest
}: ProtectedRouteProps) => {
  // Cognito `slotcheck-enterprise-reporting` is enforced in-page (403).
  // No feature-flag kill switch — access = Cognito group only.
  return (
    <Route
      path={path}
      {...rest}
      render={(props) => {
        return <Comp {...props} />;
      }}
    />
  );
};

export const ProtectedRouteCloakTheEgms = ({
  component: Comp,
  path,
  ...rest
}: ProtectedRouteProps) => {
  // Cognito `slotcheck-enterprise-cloakTheEgms` is enforced in-page (403).
  return (
    <Route
      path={path}
      {...rest}
      render={(props) => {
        return <Comp {...props} />;
      }}
    />
  );
};

export const ProtectedRouteEmulatePlayers = ({
  component: Comp,
  path,
  ...rest
}: ProtectedRouteProps) => {
  // Cognito `slotcheck-enterprise-emulatePlayers` — refuse deep links without the role.
  // In-page 403 remains as a second check (do not copy the Reports pass-through wrapper).
  const hasEmulatePlayers = useSelector<IState>((state) =>
    state.app.persistedState.userInfo?.groups?.includes('slotcheck-enterprise-emulatePlayers')
  );

  return (
    <Route
      path={path}
      {...rest}
      render={(props) => {
        return hasEmulatePlayers ? <Comp {...props} /> : <Redirect to="/" />;
      }}
    />
  );
};

export const ProtectedRouteSystemDashboard = ({
  component: Comp,
  path,
  ...rest
}: ProtectedRouteProps) => {
  // Cognito `slotcheck-enterprise-systemDashboard` — refuse deep links without the role.
  const hasSystemDashboard = useSelector<IState>((state) =>
    state.app.persistedState.userInfo?.groups?.includes('slotcheck-enterprise-systemDashboard')
  );

  return (
    <Route
      path={path}
      {...rest}
      render={(props) => {
        return hasSystemDashboard ? <Comp {...props} /> : <Redirect to="/" />;
      }}
    />
  );
};

export const ProtectedRouteCreateCasinoAccount = ({
  component: Comp,
  path,
  ...rest
}: ProtectedRouteProps) => {
  // Same groups as SideMenu Add Casino Account / CreateCasinoUser API.
  const hasCreateCasinoAccount = useSelector<IState>((state) =>
    canCreateCasinoAccount(state.app.persistedState.userInfo?.groups || [])
  );

  return (
    <Route
      path={path}
      {...rest}
      render={(props) => {
        return hasCreateCasinoAccount ? <Comp {...props} /> : <Redirect to="/" />;
      }}
    />
  );
};

export const ProtectedRouteDeleteCasinoAccounts = ({
  component: Comp,
  path,
  ...rest
}: ProtectedRouteProps) => {
  // Cognito `slotcheck-enterprise-deleteCasinoAccounts` — refuse deep links without the role.
  const hasDeleteCasinoAccounts = useSelector<IState>((state) =>
    canAccessDeleteCasinoAccounts(state.app.persistedState.userInfo?.groups || [])
  );

  return (
    <Route
      path={path}
      {...rest}
      render={(props) => {
        return hasDeleteCasinoAccounts ? <Comp {...props} /> : <Redirect to="/" />;
      }}
    />
  );
};

export const ProtectedRouteManufacturersManagement = ({
  component: Comp,
  path,
  ...rest
}: ProtectedRouteProps) => {
  // Cognito `slotcheck-enterprise-manufacturersManagement` — refuse deep links without the role.
  const hasManufacturersManagement = useSelector<IState>((state) =>
    canAccessManufacturersManagement(state.app.persistedState.userInfo?.groups || [])
  );

  return (
    <Route
      path={path}
      {...rest}
      render={(props) => {
        return hasManufacturersManagement ? <Comp {...props} /> : <Redirect to="/" />;
      }}
    />
  );
};

export const ProtectedRouteGameNamesManagement = ({
  component: Comp,
  path,
  ...rest
}: ProtectedRouteProps) => {
  // Cognito `slotcheck-enterprise-gameNamesManagement` — refuse deep links without the role.
  const hasGameNamesManagement = useSelector<IState>((state) =>
    canAccessGameNamesManagement(state.app.persistedState.userInfo?.groups || [])
  );

  return (
    <Route
      path={path}
      {...rest}
      render={(props) => {
        return hasGameNamesManagement ? <Comp {...props} /> : <Redirect to="/" />;
      }}
    />
  );
};
